// Admin Tasks — Steve's personal todo list.
//
// Source-of-truth reference: /var/www/kitchenos/admin-standalone.html
//   widget renderer:   `renderTodos` / `addTodo` / `toggleTodo` (legacy globals)
//   data:              /var/www/kitchenos-api/data/admin_todos.json
//
// Backend (verified in /var/www/kitchenos-api/server.js, line ~1068):
//   GET    /api/admin/todos           — list
//   POST   /api/admin/todos           — { text, priority? } -> ServerTodo
//   PATCH  /api/admin/todos/:id       — partial update (done, text, retailerId, priority)
//   DELETE /api/admin/todos/:id       — remove
//
// The server model is small: { id, text, done, priority, createdAt }.
// We additionally allow a `retailerId` field round-tripping through PATCH so
// admins can tag a task with a retailer. The server passes unknown keys
// through via { ...todos[idx], ...req.body }, so this just works.

import { ApiError } from "@tkos/api-client";

import { registerPage } from "../../shell/router";
import type { Page, PageContext, PageCleanup } from "../../shell/types";

import "./styles.css";

// ── Types ────────────────────────────────────────────────────────────────────

type Priority = "low" | "normal" | "high";

interface ServerTodo {
  id: string;
  text: string;
  done: boolean;
  priority?: Priority | string;
  retailerId?: string | null;
  createdAt?: string;
}

interface Todo {
  id: string;
  text: string;
  done: boolean;
  priority: Priority;
  retailerId: string | null;
  createdAt: number;
}

type FilterValue = "all" | "open" | "done";

// ── Helpers ──────────────────────────────────────────────────────────────────

const VALID_PRIORITY = new Set<Priority>(["low", "normal", "high"]);

function coercePriority(raw: unknown): Priority {
  if (typeof raw === "string" && VALID_PRIORITY.has(raw as Priority)) {
    return raw as Priority;
  }
  return "normal";
}

function coerceCreated(raw: unknown): number {
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    const t = Date.parse(raw);
    if (Number.isFinite(t)) return t;
  }
  return Date.now();
}

function normalise(t: ServerTodo): Todo {
  return {
    id: String(t.id ?? ""),
    text: String(t.text ?? "").trim(),
    done: !!t.done,
    priority: coercePriority(t.priority),
    retailerId:
      typeof t.retailerId === "string" && t.retailerId.trim() !== ""
        ? t.retailerId
        : null,
    createdAt: coerceCreated(t.createdAt),
  };
}

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

const RTF = new Intl.RelativeTimeFormat("en-GB", { numeric: "auto" });
function relTime(ms: number): string {
  const diffSec = Math.round((ms - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return RTF.format(diffSec, "second");
  if (abs < 3600) return RTF.format(Math.round(diffSec / 60), "minute");
  if (abs < 86400) return RTF.format(Math.round(diffSec / 3600), "hour");
  if (abs < 86400 * 30) return RTF.format(Math.round(diffSec / 86400), "day");
  return RTF.format(Math.round(diffSec / (86400 * 30)), "month");
}

const FILTER_OPTIONS: ReadonlyArray<{ value: FilterValue; label: string }> = [
  { value: "all", label: "All" },
  { value: "open", label: "Open" },
  { value: "done", label: "Completed" },
];

// ── Page ─────────────────────────────────────────────────────────────────────

const tasksPage: Page = {
  name: "tasks",
  label: "Tasks",
  icon: "✅",

  mount(container: HTMLElement, ctx: PageContext): PageCleanup {
    const ac = new AbortController();
    let todos: Todo[] = [];
    let filter: FilterValue = "open";
    let loading = true;
    let error: string | null = null;

    const root = document.createElement("section");
    root.className = "atask-page";
    root.innerHTML = `
      <header class="atask-header">
        <h1>Admin Tasks</h1>
        <p>Personal admin todos &mdash; tag a retailer to link it to the Retailers page.</p>
      </header>

      <div class="atask-toolbar">
        <input class="adm-input atask-new-input" data-role="new-input" type="text" placeholder="Add a task and press Enter…" />
        <select class="adm-select atask-new-priority" data-role="new-priority">
          <option value="low">Low</option>
          <option value="normal" selected>Normal</option>
          <option value="high">High</option>
        </select>
        <button class="adm-btn" type="button" data-role="add">Add</button>
        <span class="atask-spacer"></span>
        <select class="adm-select atask-filter" data-role="filter" aria-label="Filter">
          ${FILTER_OPTIONS.map(
            (o) => `<option value="${o.value}">${esc(o.label)}</option>`,
          ).join("")}
        </select>
      </div>

      <div class="atask-list" data-role="list" aria-live="polite"></div>
    `;
    container.appendChild(root);

    const newInput = root.querySelector<HTMLInputElement>("[data-role='new-input']")!;
    const newPriority = root.querySelector<HTMLSelectElement>("[data-role='new-priority']")!;
    const addBtn = root.querySelector<HTMLButtonElement>("[data-role='add']")!;
    const filterEl = root.querySelector<HTMLSelectElement>("[data-role='filter']")!;
    const listEl = root.querySelector<HTMLDivElement>("[data-role='list']")!;

    filterEl.value = filter;

    function render(): void {
      if (loading) {
        listEl.innerHTML = `<div class="adm-loading">Loading tasks…</div>`;
        return;
      }
      if (error) {
        listEl.innerHTML = `<div class="adm-error">${esc(error)}</div>`;
        return;
      }
      const visible = todos.filter((t) => {
        if (filter === "all") return true;
        if (filter === "open") return !t.done;
        return t.done;
      });
      if (visible.length === 0) {
        listEl.innerHTML = `<div class="adm-empty">No tasks. Add one above.</div>`;
        return;
      }
      listEl.innerHTML = visible
        .map(
          (t) => `
        <div class="atask-row${t.done ? " atask-done" : ""}" data-id="${esc(t.id)}">
          <label class="atask-check">
            <input type="checkbox" data-role="toggle" ${t.done ? "checked" : ""} />
          </label>
          <div class="atask-body">
            <div class="atask-text">${esc(t.text)}</div>
            <div class="atask-meta">
              <span class="adm-pill atask-pri atask-pri-${t.priority}">${esc(t.priority)}</span>
              ${
                t.retailerId
                  ? `<a class="atask-retailer-link" data-role="retailer" data-retailer-id="${esc(t.retailerId)}" href="#/retailers">Retailer: ${esc(t.retailerId)}</a>`
                  : `<button class="atask-tag-btn" type="button" data-role="tag">+ tag retailer</button>`
              }
              <span class="atask-time">${esc(relTime(t.createdAt))}</span>
            </div>
          </div>
          <button class="adm-btn adm-btn-ghost atask-del" type="button" data-role="delete" aria-label="Delete">×</button>
        </div>`,
        )
        .join("");
    }

    async function load(): Promise<void> {
      loading = true;
      error = null;
      render();
      try {
        const raw = await ctx.api<ServerTodo[]>("/admin/todos", { signal: ac.signal });
        todos = (Array.isArray(raw) ? raw : []).map(normalise);
        loading = false;
        render();
      } catch (err) {
        if (isAbortError(err)) return;
        loading = false;
        error = err instanceof ApiError ? `Couldn't load tasks (${err.status}).` : "Couldn't load tasks.";
        render();
      }
    }

    async function addTodo(text: string, priority: Priority): Promise<void> {
      if (!text.trim()) return;
      try {
        const created = await ctx.api<ServerTodo>("/admin/todos", {
          method: "POST",
          body: JSON.stringify({ text: text.trim(), priority }),
          signal: ac.signal,
        });
        todos = [normalise(created), ...todos];
        newInput.value = "";
        render();
      } catch (err) {
        if (isAbortError(err)) return;
        ctx.showToast("Couldn't add task", "error");
      }
    }

    async function patchTodo(id: string, patch: Partial<ServerTodo>): Promise<void> {
      try {
        const updated = await ctx.api<ServerTodo>(`/admin/todos/${encodeURIComponent(id)}`, {
          method: "PATCH",
          body: JSON.stringify(patch),
          signal: ac.signal,
        });
        const n = normalise(updated);
        todos = todos.map((t) => (t.id === id ? n : t));
        render();
      } catch (err) {
        if (isAbortError(err)) return;
        ctx.showToast("Couldn't update task", "error");
      }
    }

    async function deleteTodo(id: string): Promise<void> {
      try {
        await ctx.api<{ ok: true }>(`/admin/todos/${encodeURIComponent(id)}`, {
          method: "DELETE",
          signal: ac.signal,
        });
        todos = todos.filter((t) => t.id !== id);
        render();
      } catch (err) {
        if (isAbortError(err)) return;
        ctx.showToast("Couldn't delete task", "error");
      }
    }

    function submitNew(): void {
      const text = newInput.value;
      const priority = coercePriority(newPriority.value);
      void addTodo(text, priority);
    }

    const onAddClick = (): void => submitNew();
    const onNewKey = (e: KeyboardEvent): void => {
      if (e.key === "Enter") submitNew();
    };
    const onFilter = (): void => {
      filter = (filterEl.value as FilterValue) || "all";
      render();
    };
    const onListClick = (e: Event): void => {
      const target = e.target as HTMLElement;
      const row = target.closest<HTMLElement>(".atask-row");
      if (!row) return;
      const id = row.dataset.id ?? "";
      if (target.matches("[data-role='toggle']")) {
        const checked = (target as HTMLInputElement).checked;
        void patchTodo(id, { done: checked });
        return;
      }
      if (target.matches("[data-role='delete']")) {
        void deleteTodo(id);
        return;
      }
      if (target.matches("[data-role='tag']")) {
        const rid = window.prompt("Retailer ID to tag:")?.trim();
        if (rid) void patchTodo(id, { retailerId: rid });
        return;
      }
      if (target.matches("[data-role='retailer']")) {
        const rid = target.getAttribute("data-retailer-id");
        if (rid) {
          e.preventDefault();
          ctx.setCurrentRetailerId(rid);
          ctx.navigate("retailers");
        }
      }
    };

    addBtn.addEventListener("click", onAddClick);
    newInput.addEventListener("keydown", onNewKey);
    filterEl.addEventListener("change", onFilter);
    listEl.addEventListener("click", onListClick);

    void load();

    return () => {
      ac.abort();
      addBtn.removeEventListener("click", onAddClick);
      newInput.removeEventListener("keydown", onNewKey);
      filterEl.removeEventListener("change", onFilter);
      listEl.removeEventListener("click", onListClick);
      root.remove();
    };
  },
};

registerPage(tasksPage);
export default tasksPage;
